import * as React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ColorSwatchOption {
    id: string;
    label: string;
    value: string;
}

export interface ColorPaletteProps {
    options: ColorSwatchOption[];
    selectedId?: string;
    correctId?: string;
    onSelect?: (id: string) => void;
    disabled?: boolean;
    showFeedback?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const sizeClasses = {
    sm: 'h-7 w-7',
    md: 'h-9 w-9',
    lg: 'h-11 w-11',
};

export const ColorPalette: React.FC<ColorPaletteProps> = ({
    options,
    selectedId,
    correctId,
    onSelect,
    disabled = false,
    showFeedback = false,
    size = 'md',
    className,
}) => {
    const sizeClass = sizeClasses[size];
    const iconSize = size === 'sm' ? 12 : size === 'md' ? 14 : 16;

    return (
        <div className={cn('flex flex-wrap gap-2', className)}>
            {options.map((opt) => {
                const isSelected = selectedId === opt.id;
                const isCorrect = showFeedback && correctId === opt.id;
                const isIncorrect = showFeedback && selectedId === opt.id && correctId && correctId !== opt.id;

                return (
                    <button
                        key={opt.id}
                        type="button"
                        title={opt.label}
                        disabled={disabled}
                        onClick={() => !disabled && onSelect?.(opt.id)}
                        className={cn(
                            'rounded-full border-2 transition-all duration-200 flex items-center justify-center shrink-0',
                            sizeClass,
                            disabled && 'opacity-50 cursor-not-allowed',
                            !disabled && 'cursor-pointer hover:scale-110',
                            isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                            isCorrect && 'border-success',
                            isIncorrect && 'border-destructive',
                            !isSelected && !isCorrect && !isIncorrect && 'border-border'
                        )}
                        style={{ backgroundColor: opt.value }}
                        aria-label={opt.label}
                        aria-pressed={isSelected}
                    >
                        {showFeedback && isCorrect && (
                            <Check
                                className="text-white drop-shadow-md"
                                style={{ width: iconSize, height: iconSize }}
                                strokeWidth={3}
                            />
                        )}
                        {showFeedback && isIncorrect && (
                            <X
                                className="text-white drop-shadow-md"
                                style={{ width: iconSize, height: iconSize }}
                                strokeWidth={3}
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );
};

ColorPalette.displayName = 'ColorPalette';
